import { useQuery } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";
import apiV2 from "api-v2";
import type { CompletionState } from "../types/award.type";

const STALE_TIME = 30_000;

/** React Query key for the evaluation completion summary. */
export const evaluationCompletionQueryKey = (rfxId: number, evalId: number) =>
	["rfx", rfxId, "evaluation", evalId, "completion"] as const;

const unwrap = <T>(responseData: unknown): T => {
	if (
		responseData &&
		typeof responseData === "object" &&
		"data" in responseData
	) {
		return (responseData as Record<string, unknown>).data as T;
	}
	return responseData as T;
};

const getCompletion = async (
	rfxId: number,
	evalId: number
): Promise<CompletionState> => {
	const response = await apiV2.get(
		`/rfx/${rfxId}/evaluation/${evalId}/completion`
	);
	return unwrap<CompletionState>(response.data);
};

interface UseEvaluationCompletionResult {
	completion: CompletionState | undefined;
	isLoading: boolean;
}

/**
 * Reads the evaluation completion summary (`GET /completion`).
 *
 * The endpoint is delivered by TASK-003 (T003, in flight). Until it is live the
 * query will simply not resolve; callers should fall back to the `completion`
 * field on the awards payload (`useAwards`) for the banner state.
 */
export const useEvaluationCompletion = (
	rfxId: number | undefined,
	evalId: number | undefined
): UseEvaluationCompletionResult => {
	const query: UseQueryResult<CompletionState> = useQuery({
		queryKey: evaluationCompletionQueryKey(rfxId ?? 0, evalId ?? 0),
		queryFn: () => getCompletion(rfxId!, evalId!),
		enabled: !!rfxId && !!evalId,
		staleTime: STALE_TIME,
	});

	return {
		completion: query.data,
		isLoading: query.isLoading,
	};
};
